import { HttpErrorResponse } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { ToasterService } from './toaster.service';

@Injectable({ providedIn: 'root' })
export class ErrorHandlerService {
  constructor(private toastr: ToasterService) { }

  handleApiError(error: any): boolean {
    if (!error) return true;

    if (!(error instanceof HttpErrorResponse)) {
      this.toastr.serverError('An unknown error occurred.', 500);
      return true;
    }

    const status = error.status;

    // 400 - Validation errors (keep dialog open)
    if (status === 400 && Array.isArray(error.error?.errors)) {
      const errors = error.error.errors;

      const message =
        errors.length === 1
          ? `${errors[0].field ?? ''}${errors[0].field ? ': ' : ''}${errors[0].message}`
          : errors.map((e: any) => `• ${e.field ? e.field + ': ' : ''}${e.message}`).join('\n');

      this.toastr.clientError(message, 400);
      return false; // user needs to fix validation issues
    }

    // 401 / 403 - interceptor handles redirect
    if (status === 401 || status === 403) {
      return true;
    }

    // Network error
    if (status === 0) {
      this.toastr.serverError(
        'Unable to connect to the server. Please check your internet connection.',
        0,
      );
      return true;
    }

    // 500 - Server error
    if (status === 500) {
      this.toastr.serverError(
        'Server error (500): An unexpected error occurred. Please contact the administrator.',
        500,
      );
      return true;
    }

    // Other 4xx errors
    if (status >= 400 && status < 500) {
      this.toastr.clientError(
        error.error?.detail || error.error?.message || 'Something went wrong.',
        status,
      );
      return true;
    }

    // Other 5xx errors
    if (status >= 500) {
      this.toastr.serverError(error.error?.detail || 'A server error occurred.', status);
      return true;
    }

    this.toastr.serverError('An unexpected error occurred.', 500);
    return true;
  }

  // QZ Tray print failures (e.g. printer not connected/found) are client/device
  // issues, not HttpErrorResponses, so they must not be routed through handleApiError
  // (which would label them a "Server Error").
  handlePrintError(error: any): void {
    this.toastr.error(this.getPrintErrorMessage(error));
  }

  private getPrintErrorMessage(error: any): string {
    const rawMessage: string =
      error?.message ?? (typeof error === 'string' ? error : '') ?? '';
    const message = rawMessage.toLowerCase();

    if (message.includes('find printer') || message.includes('printer not found')) {
      return 'The selected printer could not be found. Please check that it is connected, powered on, and configured correctly.';
    }

    if (message.includes('connect') || message.includes('websocket') || message.includes('qz')) {
      return 'Unable to connect to the printer. Please make sure QZ Tray is running and the printer is connected.';
    }

    return 'Unable to print. Please check that the printer is connected and try again.';
  }
}
